import { Box, Multiselect, Select, SpaceBetween } from '@amzn/awsui-components-core';
import { MultiselectProps } from '@amzn/awsui-components-core/multiselect';
import { SelectProps } from '@amzn/awsui-components-core/select';
import React, { useCallback, useMemo } from 'react';
import { CustomBadge } from 'src/components/common/CustomBadge';
import { useAppSelector } from 'src/store/hooks';
import {
  selectActiveProductLineOptionsFromMinimalHierarchy,
  selectActiveProductLineOptionsWithRpnCountFromMinimalHierarchy,
  selectGroupedProductLineOptionsFromMinimalHierarchy,
  selectGroupedProductLineOptionsWithRpnCountFromMinimalHierarchy,
} from 'src/store/selectors/productHierarchySelectors';

/**
 * Generates a dynamic placeholder text for the multiselect based on selected options
 *
 * @param selectedOptions - Array of currently selected options
 * @param defaultPlaceholder - Default placeholder text when nothing is selected
 * @returns Formatted placeholder text showing selection summary
 *
 * @example
 * // No selection: "Select product lines"
 * // One selection: "Echo"
 * // Multiple selections: "Echo + 2 Others"
 */
const getMultiSelectPlaceholderValue = (
  selectedOptions: readonly MultiselectProps.Option[],
  defaultPlaceholder: string
): string => {
  if (!selectedOptions || selectedOptions.length === 0) {
    return defaultPlaceholder;
  }

  if (selectedOptions.length === 1) {
    return selectedOptions[0].label || '';
  }

  if (selectedOptions.length > 1) {
    const othersCount = selectedOptions.length - 1;
    return `${selectedOptions[0].label || ''} + ${othersCount} ${othersCount === 1 ? 'Other' : 'Others'}`;
  }

  return defaultPlaceholder;
};

/**
 * Props for ProductLineSelect component (single-select mode)
 */
interface SingleSelectProps {
  /** Mode: single-select */
  multiple?: false;
  /** Currently selected product line option */
  selectedOption: SelectProps.Option | null;
  /** Callback when product line selection changes (single option) */
  onChange: (selectedOption: SelectProps.Option | null) => void;
}

/**
 * Props for ProductLineSelect component (multi-select mode)
 */
interface MultiSelectProps {
  /** Mode: multi-select */
  multiple: true;
  /** Currently selected product line options (array) */
  selectedOptions: readonly MultiselectProps.Option[];
  /** Callback when product line selection changes (array of options) */
  onChange: (selectedOptions: readonly MultiselectProps.Option[]) => void;
}

/**
 * Common props for both single and multi-select modes
 */
interface CommonProps {
  /** Whether to group options by product_line_group from backend (default: false) */
  grouped?: boolean;
  /** Whether to show RPN count as a tag next to each product line option (default: false) */
  showRpnCount?: boolean;
  /** Optional CSS class name for styling */
  className?: string;
  /** Placeholder text when no option is selected */
  placeholder?: string;
  /** Aria label for accessibility */
  ariaLabel?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether dropdown should expand to viewport */
  expandToViewport?: boolean;
  /** Type of filtering to apply */
  filteringType?: 'auto' | 'manual' | 'none';
  /** Callback when select loses focus (for form integration) */
  onBlur?: () => void;
  /** Whether field is required (for form validation) */
  ariaRequired?: boolean;
  /** Whether component is in loading state */
  isLoading?: boolean;
}

/**
 * Combined props type for ProductLineSelect
 */
type ProductLineSelectProps = CommonProps & (SingleSelectProps | MultiSelectProps);

/**
 * ProductLineSelect - A reusable product line selector component
 *
 * This component wraps the Cloudscape Select or Multiselect component and provides:
 * - Support for both single-select and multi-select modes
 * - Optional grouping by product_line_group from backend
 * - Stable remounting behavior when options change
 * - Consistent styling and behavior across the application
 * - Pre-sorted options using three-tier sorting logic (Device first, Accessories last, 0 RPNs at bottom)
 *
 * Usage (Single-select):
 * ```tsx
 * <ProductLineSelect
 *   selectedOption={selectedOption}
 *   onChange={(option) => handleChange(option)}
 *   grouped={true}
 * />
 * ```
 *
 * Usage (Multi-select):
 * ```tsx
 * <ProductLineSelect
 *   multiple={true}
 *   selectedOptions={selectedOptions}
 *   onChange={(options) => handleChange(options)}
 *   grouped={true}
 * />
 * ```
 */
export const ProductLineSelect: React.FC<ProductLineSelectProps> = (props) => {
  const {
    grouped = false,
    showRpnCount = false,
    className,
    placeholder = 'Select a product line',
    ariaLabel = 'Select product line',
    expandToViewport = false,
    filteringType = 'auto',
    onBlur,
    ariaRequired,
    disabled = false,
    isLoading = false,
  } = props;

  // Select appropriate options based on grouped and showRpnCount flags
  const flatOptions = useAppSelector(selectActiveProductLineOptionsFromMinimalHierarchy);
  const flatOptionsWithRpnCount = useAppSelector(selectActiveProductLineOptionsWithRpnCountFromMinimalHierarchy);
  const groupedOptions = useAppSelector(selectGroupedProductLineOptionsFromMinimalHierarchy);
  const groupedOptionsWithRpnCount = useAppSelector(selectGroupedProductLineOptionsWithRpnCountFromMinimalHierarchy);

  // Determine which options to use based on grouped and showRpnCount flags
  const productLineOptions = useMemo(() => {
    if (grouped) {
      return showRpnCount ? groupedOptionsWithRpnCount : groupedOptions;
    }
    return showRpnCount ? flatOptionsWithRpnCount : flatOptions;
  }, [grouped, showRpnCount, flatOptions, flatOptionsWithRpnCount, groupedOptions, groupedOptionsWithRpnCount]);

  // Generate stable key for Select/Multiselect remounting when options change
  const selectKey = useMemo(() => {
    let optionsLength: number;
    if (grouped) {
      // productLineOptions is SelectProps.OptionGroup[]
      optionsLength = (productLineOptions as SelectProps.OptionGroup[]).reduce(
        (sum, group) => sum + group.options.length,
        0
      );
    } else {
      // productLineOptions is SelectProps.Option[]
      optionsLength = (productLineOptions as SelectProps.Option[]).length;
    }
    return `select-${optionsLength}-grouped-${grouped}`;
  }, [productLineOptions, grouped]);

  // Shared rendering logic for product line option with RPN count badge
  const renderOptionContent = useCallback(
    (option: SelectProps.Option, isMultiSelect: boolean = false): React.ReactNode => {
      return (
        <Box padding={{ vertical: 'xs', left: 'l', right: 'm' }}>
          <SpaceBetween size="xxxs" direction="vertical">
            <Box fontSize="body-s" padding={{ left: isMultiSelect ? 'xxl' : undefined }}>
              {option.label}
            </Box>
            <Box>
              <Box float="left">
                <Box
                  variant="span"
                  color="text-body-secondary"
                  fontSize="body-s"
                  padding={{ left: isMultiSelect ? 'xxl' : undefined }}
                >
                  {option.description}
                </Box>
              </Box>
              <Box float="right">
                {option.labelTag && (
                  <CustomBadge variant={option.labelTag.startsWith('0') ? 'grey' : 'blue'}>
                    {option.labelTag}
                  </CustomBadge>
                )}
              </Box>
            </Box>
          </SpaceBetween>
        </Box>
      );
    },
    []
  );

  // Custom render option for Select
  const selectRenderOption: SelectProps.SelectOptionItemRenderer = useCallback(
    ({ item }) => {
      if (item.type !== 'item') return null;
      return renderOptionContent(item.option);
    },
    [renderOptionContent]
  );

  // Custom render option for Multiselect
  const multiselectRenderOption: MultiselectProps.MultiselectOptionItemRenderer = useCallback(
    ({ item }) => {
      if (item.type !== 'item') return null;
      return renderOptionContent(item.option, true);
    },
    [renderOptionContent]
  );

  // Generate dynamic placeholder for multi-select based on selected options
  const dynamicPlaceholder = useMemo(() => {
    if (props.multiple) {
      return getMultiSelectPlaceholderValue(props.selectedOptions, placeholder);
    }
    return placeholder;
  }, [props, placeholder]);

  // Render multi-select mode
  if (props.multiple) {
    return (
      <Multiselect
        key={selectKey}
        className={className}
        selectedOptions={props.selectedOptions}
        options={productLineOptions}
        onChange={({ detail }) => props.onChange(detail.selectedOptions)}
        placeholder={dynamicPlaceholder}
        ariaLabel={ariaLabel}
        expandToViewport={expandToViewport}
        filteringType={filteringType}
        loadingText="Loading product lines"
        disabled={disabled}
        statusType={isLoading ? 'loading' : 'finished'}
        hideTokens
        enableSelectAll={true}
        renderOption={showRpnCount ? multiselectRenderOption : undefined}
      />
    );
  }

  // Render single-select mode
  return (
    <Select
      key={selectKey}
      className={className}
      selectedOption={props.selectedOption}
      options={productLineOptions}
      onChange={({ detail }) => props.onChange(detail.selectedOption)}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      expandToViewport={expandToViewport}
      filteringType={filteringType}
      loadingText="Loading product lines"
      disabled={disabled}
      onBlur={onBlur}
      ariaRequired={ariaRequired}
      statusType={isLoading ? 'loading' : 'finished'}
      renderOption={showRpnCount ? selectRenderOption : undefined}
    />
  );
};
